/**
 * 运营商配置标签页
 * 管理运营商创建用户的成本和赠金配置
 */

import { useState, useEffect } from 'react'
import { SystemParameter } from '@/types'
import './OperatorTab.css'

interface OperatorTabProps {
  parameters: SystemParameter[]
  onUpdate: (key: string, value: string) => Promise<void>
}

interface UserCreationConfig {
  cost: number
  bonus: number
}

export default function OperatorTab({ parameters, onUpdate }: OperatorTabProps) {
  const [config, setConfig] = useState<UserCreationConfig>({ cost: 100, bonus: 50 })
  const [isEditing, setIsEditing] = useState(false)
  const [editConfig, setEditConfig] = useState<UserCreationConfig>({ cost: 100, bonus: 50 })
  const [saving, setSaving] = useState(false)

  useEffect(() => {
    // 从参数中加载配置
    const userCreationParam = parameters.find(p => p.param_key === 'user_creation_config')
    if (userCreationParam) {
      try {
        const parsed = JSON.parse(userCreationParam.param_value)
        setConfig(parsed)
        setEditConfig(parsed)
      } catch (error) {
        console.error('Failed to parse user_creation_config:', error)
      }
    }
  }, [parameters])

  const handleEdit = () => {
    setEditConfig({ ...config })
    setIsEditing(true)
  }

  const handleCancel = () => {
    setEditConfig({ ...config })
    setIsEditing(false)
  }

  const handleSave = async () => {
    setSaving(true)
    try {
      const newValue = JSON.stringify(editConfig)
      await onUpdate('user_creation_config', newValue)
      setConfig(editConfig)
      setIsEditing(false)
    } catch (error) {
      console.error('Failed to save config:', error)
    } finally {
      setSaving(false)
    }
  }

  return (
    <div className="operator-tab">
      <div className="tab-section">
        <div className="section-header">
          <h2>用户创建配置</h2>
          <p className="section-description">
            配置运营商创建用户时的成本和赠金规则
          </p>
        </div>

        <div className="config-card">
          <div className="config-header">
            <div className="config-title">
              <span className="config-icon">👤</span>
              <div>
                <h3>创建用户配置</h3>
                <p className="config-subtitle">运营商创建用户的成本和赠金设置</p>
              </div>
            </div>
            {!isEditing && (
              <button className="btn-edit" onClick={handleEdit}>
                编辑
              </button>
            )}
          </div>

          <div className="config-body">
            {isEditing ? (
              // 编辑模式
              <div className="config-form">
                <div className="form-group">
                  <label htmlFor="cost">
                    <span className="label-text">创建成本</span>
                    <span className="label-hint">运营商创建一个用户需要花费的金额</span>
                  </label>
                  <div className="input-group">
                    <span className="input-prefix">$</span>
                    <input
                      id="cost"
                      type="number"
                      min="0"
                      step="0.01"
                      value={editConfig.cost}
                      onChange={(e) => setEditConfig({ ...editConfig, cost: parseFloat(e.target.value) || 0 })}
                      className="form-input"
                    />
                  </div>
                </div>

                <div className="form-group">
                  <label htmlFor="bonus">
                    <span className="label-text">返佣金额</span>
                    <span className="label-hint">新用户创建后自动获得的返佣</span>
                  </label>
                  <div className="input-group">
                    <span className="input-prefix">$</span>
                    <input
                      id="bonus"
                      type="number"
                      min="0"
                      step="0.01"
                      value={editConfig.bonus}
                      onChange={(e) => setEditConfig({ ...editConfig, bonus: parseFloat(e.target.value) || 0 })}
                      className="form-input"
                    />
                  </div>
                </div>

                <div className="form-actions">
                  <button 
                    className="btn-cancel" 
                    onClick={handleCancel}
                    disabled={saving}
                  >
                    取消
                  </button>
                  <button 
                    className="btn-save" 
                    onClick={handleSave}
                    disabled={saving}
                  >
                    {saving ? '保存中...' : '保存'}
                  </button>
                </div>
              </div>
            ) : (
              // 查看模式
              <div className="config-display">
                <div className="config-item">
                  <div className="config-item-label">
                    <span className="item-icon">💵</span>
                    <span>创建成本</span>
                  </div>
                  <div className="config-item-value">
                    <span className="value-amount">${config.cost.toFixed(2)}</span>
                    <span className="value-hint">每创建一个用户</span>
                  </div>
                </div>

                <div className="config-divider"></div>

                <div className="config-item">
                  <div className="config-item-label">
                    <span className="item-icon">💸</span>
                    <span>返佣金额</span>
                  </div>
                  <div className="config-item-value">
                    <span className="value-amount">${config.bonus.toFixed(2)}</span>
                    <span className="value-hint">新用户返佣</span>
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* 说明信息 */}
          <div className="config-footer">
            <div className="info-box">
              <div className="info-header">
                <span className="info-icon">ℹ️</span>
                <span className="info-title">配置说明</span>
              </div>
              <ul className="info-list">
                <li>
                  <strong>创建成本</strong>：运营商创建用户时，从运营商账户余额中扣除的金额
                </li>
                <li>
                  <strong>返佣金额</strong>：新用户创建后，自动充值到用户赠金余额的返佣金额
                </li>
                <li>
                  运营商余额不足时，无法创建新用户
                </li>
                <li>
                  所有资金变动都会记录在资金流水中
                </li>
              </ul>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}
